import {
  faCircleNodes,
  faRobot,
  faStar,
} from "@fortawesome/pro-regular-svg-icons";
import { faStar as faStarSolid } from "@fortawesome/pro-solid-svg-icons";
import { Badge, Button, Icon, Message } from "@intility/bifrost-react";
import type { Columns } from "@intility/gjallarbru";
import { useCallback, useMemo, useState } from "react";
import { useClients } from "../../api/clients";
import { usePlatforms } from "../../api/platforms";
import type { ClientListItem } from "../../api/types";
import { auth } from "../../auth";
import { ClientsTable } from "../../components/tables/ClientsTable";
import { FROM_MYHUB } from "../../lib/fromMyHub";
import ClientCard from "./MyClients-ClientCard";
import { type HubView, MyHubSection } from "./MyHubSection";
import s from "./MyPlatforms.module.css";
import { readStarred, starredClientsKey, writeStarred } from "./starredStorage";

type Row = { row: { original: ClientListItem } };

// The clients the user owns or maintains, as in My platforms: Favorites on top
// and every client under it, each as a table or as cards.
export default function MyClients() {
  const { data: clients } = useClients();
  const { data: platforms } = usePlatforms();
  const [allView, setAllView] = useState<HubView>("list");
  const [favouritesView, setFavouritesView] = useState<HubView>("grid");
  // Stars live in this browser's localStorage, one list per signed-in user.
  const storageKey = starredClientsKey(auth.keycloak.tokenParsed?.sub);
  const [starred, setStarred] = useState(() => readStarred(storageKey));

  // The list says what the user's role is in each client.
  const myClients = useMemo(
    () =>
      (clients ?? []).filter(
        (c) => c.userRole === "owner" || c.userRole === "maintainer",
      ),
    [clients],
  );
  const favourites = useMemo(
    () => myClients.filter((c) => starred.has(c.id)),
    [myClients, starred],
  );
  const platformNameById = useMemo(
    () => new Map((platforms ?? []).map((p) => [p.id, p.name])),
    [platforms],
  );

  const toggle = useCallback(
    (id: string) => {
      const next = new Set(starred);
      if (!next.delete(id)) next.add(id);
      setStarred(next);
      writeStarred(storageKey, next);
    },
    [starred, storageKey],
  );

  // The favourite star sits before the table's own columns.
  const columnsBefore = useMemo<Columns<ClientListItem>>(
    () => [
      {
        // An accessor with a value keeps the table from rendering its empty
        // "–" placeholder in place of the Cell.
        accessorKey: "id",
        id: "favourite",
        Cell: ({ row }: Row) => {
          const { id, name } = row.original;
          const isStarred = starred.has(id);
          return (
            <Button
              state="neutral"
              variant="flat"
              noPadding
              aria-label={`Favourite ${name}`}
              aria-pressed={isStarred}
              onClick={() => toggle(id)}
            >
              <Icon
                className={isStarred ? "bfc-warning" : "bfc-base-2"}
                icon={isStarred ? faStarSolid : faStar}
              />
            </Button>
          );
        },
        enableColumnFilter: false,
        // Keeps the star out of the Columns menu and pinned first.
        enableColumnOrdering: false,
        enableHiding: false,
        enableSorting: false,
        // The name shows in the Columns menu; the header cell itself stays empty.
        header: "Favorites",
        Header: () => null,
        // Fixed width (24px padding + 18px icon + 24px padding).
        grow: false,
        size: 66,
      },
    ],
    [starred, toggle],
  );

  const renderCards = (list: ClientListItem[]) => (
    <div className={s.cardsArea}>
      <div className={s.cards}>
        {list.map((client) => (
          <ClientCard
            key={client.id}
            client={client}
            role={client.userRole}
            platformName={
              client.platformId
                ? platformNameById.get(client.platformId)
                : undefined
            }
            onToggleStar={() => toggle(client.id)}
            starred={starred.has(client.id)}
          />
        ))}
      </div>
    </div>
  );

  const renderTable = (list: ClientListItem[], tableName: string) => (
    <ClientsTable
      clients={list}
      columnsBefore={columnsBefore}
      enablePagination={false}
      name={tableName}
      navigateState={FROM_MYHUB}
      rowClassName={s.row}
      showCreated={false}
      toolbarRadius={false}
    />
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h2 className="bf-h2" style={{ margin: 0 }}>
          My clients
        </h2>
        <Badge pill>
          <Icon icon={faRobot} style={{ marginRight: 6 }} />
          {myClients.length}
        </Badge>
      </div>
      <p
        className="bf-large bfc-base-2"
        style={{ marginTop: 8, marginBottom: 0 }}
      >
        Clients you own and maintain
      </p>
      <MyHubSection
        title="Favorites"
        icon={faStar}
        view={favouritesView}
        onViewChange={setFavouritesView}
        empty={
          <Message noIcon style={{ textAlign: "center" }}>
            No favorites selected yet. Click the star on a client to make it a
            favorite.
          </Message>
        }
        isEmpty={favourites.length === 0}
        list={renderTable(favourites, "myhub-client-favourites")}
        cards={renderCards(favourites)}
      />
      <MyHubSection
        title="All clients"
        icon={faCircleNodes}
        view={allView}
        onViewChange={setAllView}
        isEmpty={myClients.length === 0}
        list={renderTable(myClients, "myhub-clients")}
        cards={renderCards(myClients)}
      />
    </div>
  );
}
